import { useEffect, useRef } from 'react'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { observe } from '~/services/observability/observe'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'

// PHI-safe: only flags, counts, and lengths — never raw numbers or search text.
export type WebPhoneRenderSnapshot = Record<string, string | number | boolean>

// Detect the "Maximum update depth exceeded" render loop across BOTH builds.
// Dev emits the full text; a production build strips it to the minified form
// ("Minified React error #185; visit https://react.dev/errors/185 ..."), so the
// prod environment this targets (WEB-APP-PRODUCTION-6ZB) only ever matches on
// the stable error CODE — hence the 185 check, not just the dev string.
const isMaxUpdateDepthError = (message: string): boolean =>
  message.includes('Maximum update depth exceeded') ||
  /(?:error #|invariant=)185\b/.test(message)
// The loop fires the same error many times per second before it settles; one
// snapshot per burst is enough and avoids amplifying a page that's already sick.
const CAPTURE_COOLDOWN_MS = 30 * 1000

/**
 * Captures a render-state snapshot when a React #185 render loop fires on
 * /web-phone. The loop isn't in the frame Sentry blames (WEB-APP-PRODUCTION-6ZB);
 * this records the surrounding state so the trigger can be pinpointed from
 * PostHog without a session replay (those sessions are sampled out).
 *
 * Listens on window 'error' because the loop reaches window.onerror rather than
 * a React error boundary. Reads the latest snapshot from a ref and only ever
 * fires-and-forgets telemetry, so it can't feed the loop it observes.
 */
export const useWebPhoneRenderLoopDiagnostics = (
  snapshot: WebPhoneRenderSnapshot,
) => {
  const snapshotRef = useRef(snapshot)
  snapshotRef.current = snapshot
  const lastCapturedAtRef = useRef(0)

  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      const message = event.message || event.error?.message || ''
      if (!isMaxUpdateDepthError(message)) {
        return
      }
      if (!window.location.pathname.includes(ApplicationRoutes.WEB_PHONE)) {
        return
      }

      const now = Date.now()
      if (now - lastCapturedAtRef.current < CAPTURE_COOLDOWN_MS) {
        return
      }
      lastCapturedAtRef.current = now

      // Guard the telemetry so a throwing observe call can't surface as a new
      // uncaught error — the diagnostic must never add noise to the issue it's
      // investigating.
      try {
        const data = { ...snapshotRef.current }
        observe.track(
          AnalyticsNamespace.WEB_PHONE,
          WebPhoneAnalyticsEvents.MaxUpdateDepthDetected,
          data,
        )
        // Also trail it on the Sentry session so the repeated #185 events in the
        // same burst carry the state that the first one lacked.
        observe.sentry.addBreadcrumb({
          category: 'webphone',
          level: 'error',
          message: 'Render loop: Maximum update depth exceeded',
          data,
        })
      } catch {
        // Diagnostics are best-effort; swallow.
      }
    }

    window.addEventListener('error', handler)
    return () => window.removeEventListener('error', handler)
  }, [])
}
